import React, { useState, useEffect, memo, useRef } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { useNavigate, useLocation } from 'react-router-dom';
import Loader from '../../common/Loader/Loader';
import { Group, updateSelectedAsset, setSelectedGroupByName, Asset as WellAsset } from '../AssetGroupsSlice';
import {
  fetchAssetGroupSearchHistory,
  updateWellSearchHistory,
} from '../../../components/slices/AssetGroupSearchSlice';
import { UpdateWellSearchHistoryParams } from '../../../models/UpdateWellSearchHistoryParamsModel';
import { WellSearchObjectModel } from '../../../models/WellSearchModel';

import { ReactComponent as SearchIcon } from '../../../images/search-md.svg';
import { ReactComponent as WellEnabledIcon } from '../../../images/well-enabled-icon.svg';
import { ReactComponent as WellDisabledIcon } from '../../../images/well-disabled-icon.svg';
import { ReactComponent as WellGroupIcon } from '../../../images/well-group-icon.svg';
import { ReactComponent as RecentIcon } from '../../../images/clock-rewind.svg';

import { ReactComponent as DarkChemicalInjection } from '../../../images/dark-chemical-injection.svg';
import { ReactComponent as RodLift } from '../../../images/dark-rod-lift.svg';
import { ReactComponent as DarkDefault } from '../../../images/dark-default-icon.svg';
import { ReactComponent as DarkGasLift } from '../../../images/dark-gas-lift.svg';
import { ReactComponent as DarkInjection } from '../../../images/dark-injection.svg';
import { ReactComponent as DarkJetLift } from '../../../images/dark-jet-lift.svg';
import { ReactComponent as DarkPCP } from '../../../images/dark-pcp.svg';
import { ReactComponent as DarkPlungerLift } from '../../../images/dark-plunger-lift.svg';
import { ReactComponent as DarkFacilityLift } from '../../../images/dark-facility.svg';
import { ReactComponent as DarkESP } from '../../../images/dark-esp.svg';
import { ReactComponent as DarkValve } from '../../../images/dark-valve.svg';
import { ReactComponent as DarkPID } from '../../../images/dark-pid.svg';
import { ReactComponent as DarkTank } from '../../../images/dark-tank.svg';
import { ReactComponent as DarkLiquidMeter } from '../../../images/dark-liquid-meter.svg';
import { ReactComponent as DarkPump } from '../../../images/dark-pump.svg';
import { ReactComponent as OrifaceGasMeter } from '../../../images/dark-oriface-gas-meter.svg';
import { setIndustryApplicationId } from '../../asset-analysis/AssetAnalysisSlice';
import './GlobalWellHistorySearch.scss';

interface GlobalWellHistorySearchProps {
  className?: string;
  onIconClick?: () => void;
  onCollapse?: () => void; // NEW
  onChange?: ({ name, id }: { name: string | undefined; id: any }) => void;
  navigateToWell?: boolean;
  shouldSyncHistory?: boolean;
  placeholderText?: string;
  showAssetsOnly?: boolean;
  inputStyle?: string;
  showWellStatus?: boolean;
  shouldKeepSelectedWell?: boolean;
  parentComponent?: string; // NEW
}

const GlobalWellHistorySearch: React.FC<GlobalWellHistorySearchProps> = ({
  className = '',
  onIconClick,
  onCollapse,
  navigateToWell = true,
  onChange,
  shouldSyncHistory = true,
  showAssetsOnly = false,
  placeholderText = 'Search wells and custom groups',
  inputStyle = '',
  showWellStatus = true,
  shouldKeepSelectedWell = false,
  parentComponent
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [showSearchResult, setShowSearchResult] = useState(false);
  const [searchResults, setSearchResults] = useState<WellSearchObjectModel[]>([]);
  const [searchValue, setSearchValue] = useState<string | undefined>('');

  const [isExpanded, setIsExpanded] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { searchHistory, loading } = useAppSelector((state: any) => state.SearchHistorySlice);
  const { groups, assets } = useAppSelector((state) => state.assetGroups);

  const currentAssetURLPath = (() => {
    const pathSegment = location.pathname.split('/')[2];
    return location.pathname.includes('/asset') ? pathSegment : 'status';
  })();
  const isRenderedfromLocationsModule = parentComponent === 'WellLocations'; // Identifying if its rendered inside Locations Module


  const flattenGroups = (groups: Group[]): Group[] => {
    let allGroups: Group[] = [];
    groups?.forEach((group) => {
      allGroups.push(group);
      if (group.childGroups && group.childGroups.length > 0) {
        allGroups = allGroups.concat(flattenGroups(group.childGroups));
      }
    });
    return allGroups;
  };

  useEffect(() => {
    if (searchHistory) {
      // Group assets by groupName and find the group with the most assets
      const groupedByGroupName =
        assets?.reduce((acc: Record<string, WellAsset[]>, asset: WellAsset) => {
          const groupName = asset.groupName || 'Unknown';
          if (!acc[groupName]) {
            acc[groupName] = [];
          }
          acc[groupName].push(asset);
          return acc;
        }, {}) || {};

      // Find the groupName with the most assets
      const groupNameWithMostAssets = Object.keys(groupedByGroupName).reduce(
        (maxGroupName: string | null, currentGroupName: string) => {
          if (!maxGroupName) return currentGroupName;
          const currentCount = groupedByGroupName[currentGroupName]?.length || 0;
          const maxCount = groupedByGroupName[maxGroupName]?.length || 0;
          return currentCount > maxCount ? currentGroupName : maxGroupName;
        },
        null,
      );

      // Get assets from the group with the most assets
      const allWellsGroupAssets = groupNameWithMostAssets ? groupedByGroupName[groupNameWithMostAssets] || [] : [];

      const mappedAssets: WellSearchObjectModel[] =
        allWellsGroupAssets?.map(
          (assetItem: WellAsset) =>
            ({
              assetName: assetItem.assetName,
              assetGUID: assetItem.assetId,
              industryApplicationId: assetItem.industryApplicationId,
              wellOpType: assetItem.wellOpType,
              enabled: assetItem.enabled,
              groupName: groupNameWithMostAssets || assetItem.groupName,
              searchType: 'Well',
            }) as WellSearchObjectModel,
        ) || [];

      const allGroups = groups ? flattenGroups(groups) : [];
      const sortedGroups =
        allGroups?.sort((a, b) => {
          const nameA = a.assetGroupName?.toLowerCase() ?? '';
          const nameB = b.assetGroupName?.toLowerCase() ?? '';
          return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        }) || [];
      const mappedGroups: WellSearchObjectModel[] =
        sortedGroups?.map(
          (group: Group) =>
            ({
              assetGroupId: group.id,
              groupName: group.assetGroupName,
              searchType: 'Group',
            }) as WellSearchObjectModel,
        ) || [];

      const searchResults = showAssetsOnly
        ? [...(mappedAssets || [])]
        : [...(searchHistory || []), ...(mappedAssets || []), ...(mappedGroups || [])];
      setSearchResults(searchResults);
    }
  }, [searchHistory, assets, groups, showAssetsOnly]);

  useEffect(() => {
    if (!isExpanded) return;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value?.length ?? 0;
      try {
        el.setSelectionRange?.(len, len);
      } catch {
        ('');
      }
    });
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded && !showSearchResult) return;
    const onOutsideCapture = (e: PointerEvent) => {
      const root = rootRef.current;
      if (!root) return;
      if (root.contains(e.target as Node)) return;
      const el = e.target as Element | null;
      if (el && el.closest?.('[data-search-keepopen]')) return;
      setIsExpanded(false);
      setShowSearchResult(false);
      onCollapse?.(); // ✅ call parent
    };
    const opts: AddEventListenerOptions = { capture: true };
    document.addEventListener('pointerdown', onOutsideCapture, opts);
    return () => document.removeEventListener('pointerdown', onOutsideCapture, opts);
  }, [isExpanded, showSearchResult, onCollapse]);

  useEffect(() => {
    if (!isExpanded && !showSearchResult) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
        setShowSearchResult(false);
        onCollapse?.(); // ✅ call parent
      }
    };
    document.addEventListener('keydown', onEsc, { capture: true });
    return () => document.removeEventListener('keydown', onEsc, { capture: true } as any);
  }, [isExpanded, showSearchResult, onCollapse]);

  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number): ((...args: Parameters<T>) => void) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const showSearchHistory = async (
    e: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => {
    let searchValue = '';
    if ('target' in e && e.target instanceof HTMLInputElement) {
      searchValue = e.target.value;
    }
    if (searchValue.length >= 3) setShowSearchResult(true);
    if (!searchValue && !showSearchResult) {
      setShowSearchResult(true);
      await dispatch(fetchAssetGroupSearchHistory());
    }
  };

  const openSearchItem = async (assetOrGroup: WellSearchObjectModel) => {
    setIsExpanded(false);
    setShowSearchResult(false);
    onCollapse?.();

    let groupId: number | undefined = assetOrGroup?.assetGroupId;

    // For wells (not groups), we need to find the group they belong to
    if (assetOrGroup.searchType !== 'Group' && assetOrGroup?.groupName) {
      const allGroups = groups ? flattenGroups(groups) : [];
      const matchingGroup = allGroups.find((g) => g.assetGroupName === assetOrGroup.groupName);
      if (matchingGroup) {
        groupId = matchingGroup.id;
      }
    }

    if (assetOrGroup.searchType === 'Group') {
      dispatch(setSelectedGroupByName({ groupName: assetOrGroup.groupName || '' }));
    }

    dispatch(
      updateSelectedAsset({
        groupId: groupId,
        assetId: assetOrGroup.searchType === 'Group' ? undefined : assetOrGroup.assetName,
        searchString: undefined,
        assetIdGuid: assetOrGroup.searchType === 'Group' ? undefined : assetOrGroup.assetGUID,
      }),
    );

    if (assetOrGroup.searchType !== 'Group' && assetOrGroup.industryApplicationId) {
      dispatch(
        setIndustryApplicationId({
          industryApplicationId: assetOrGroup.industryApplicationId,
        }),
      );
    }

    if (shouldSyncHistory) {
      const payload: UpdateWellSearchHistoryParams = {
        searchType: assetOrGroup.searchType,
        assetGUID: assetOrGroup.assetGUID,
        assetName: assetOrGroup.searchType == 'Group' ? assetOrGroup.groupName : assetOrGroup.assetName,
        industryApplicationId: assetOrGroup.industryApplicationId,
        wellOpType: assetOrGroup.wellOpType,
        groupName: assetOrGroup?.groupName,
        enabled: assetOrGroup?.enabled,
      };
      await dispatch(updateWellSearchHistory(payload));
    }

    // Navigate only after all state updates are complete
    if (navigateToWell) {
      if (assetOrGroup.searchType === 'Group') {
        navigate(`/group/customViews/${encodeURIComponent(assetOrGroup.groupName ?? '')}`);
      } else {
        const formattedGroupName = assetOrGroup?.groupName?.split(' ').join('_');
        navigate(`/asset/${currentAssetURLPath}/${assetOrGroup.assetGUID}/${formattedGroupName}`);
      }
    }

    shouldKeepSelectedWell ? setSearchValue(assetOrGroup.assetName || assetOrGroup.groupName) : setSearchValue('');

    if (onChange) {
      onChange({
        name: assetOrGroup.searchType == 'Group' ? assetOrGroup.groupName : assetOrGroup.assetName,
        id: assetOrGroup.assetGUID,
      });
    }
  };
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchValue(inputValue);
    throttledSearch(inputValue);
  };

  const performSearch = async (currentSearchValue: string) => {
    if (currentSearchValue.length === 0) {
      await dispatch(fetchAssetGroupSearchHistory());
      setShowSearchResult(true);
    } else if (currentSearchValue.length >= 3) {
      setSearchResults([]);
      const searchHistoryRecords = searchHistory?.filter(
        (item: WellSearchObjectModel) =>
          item?.assetName?.toLowerCase().includes(currentSearchValue?.trim().toLowerCase()) ||
          item?.groupName?.toLowerCase().includes(currentSearchValue?.trim().toLowerCase()),
      );
      const searchedAssets = assets?.filter((assetItem: WellAsset) =>
        assetItem.assetName?.toLowerCase().includes(currentSearchValue?.trim().toLowerCase()),
      );

      const uniqueAssets =
        searchedAssets?.reduce((acc: WellAsset[], currentAsset: WellAsset) => {
          const existingAsset = acc.find((asset) => asset.assetId === currentAsset.assetId);
          if (!existingAsset) {
            acc.push(currentAsset);
          } else if (currentAsset.groupName === 'All Wells' && existingAsset.groupName !== 'All Wells') {
            const index = acc.findIndex((asset) => asset.assetId === currentAsset.assetId);
            acc[index] = currentAsset;
          }
          return acc;
        }, []) || [];

      const mappedAssets: WellSearchObjectModel[] =
        uniqueAssets?.map(
          (assetItem: WellAsset) =>
            ({
              assetName: assetItem.assetName,
              assetGUID: assetItem.assetId,
              industryApplicationId: assetItem.industryApplicationId,
              wellOpType: assetItem.wellOpType,
              enabled: assetItem.enabled,
              groupName: assetItem.groupName,
              searchType: 'Well',
            }) as WellSearchObjectModel,
        ) || [];
      const allGroups = groups ? flattenGroups(groups) : [];
      const searchedGroups = allGroups?.filter((group: Group) =>
        group.assetGroupName?.toLowerCase().includes(currentSearchValue?.trim().toLowerCase()),
      );
      const sortedSearchedGroups =
        searchedGroups?.sort((a, b) => {
          const nameA = a.assetGroupName?.toLowerCase() ?? '';
          const nameB = b.assetGroupName?.toLowerCase() ?? '';
          return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
        }) || [];
      const mappedGroups: WellSearchObjectModel[] =
        sortedSearchedGroups?.map(
          (group: Group) =>
            ({
              assetGroupId: group.id,
              groupName: group.assetGroupName,
              searchType: 'Group',
            }) as WellSearchObjectModel,
        ) || [];

      const searchResults = showAssetsOnly
        ? [...(mappedAssets || [])]
        : [...(searchHistoryRecords || []), ...(mappedAssets || []), ...(mappedGroups || [])];

      setSearchResults(searchResults);
      setShowSearchResult(true);
    }
  };

  const clearSearch = async () => {
    setSearchValue('');
    await dispatch(fetchAssetGroupSearchHistory());
    onChange && onChange({ name: undefined, id: undefined });
    setShowSearchResult(true);
  };

  const showAssetIcon = (asset: WellSearchObjectModel) => {
    switch (asset?.industryApplicationId) {
      case 3:
        return <RodLift className={'navigation-icon'} />;
      case 4:
        return <DarkESP className={'navigation-icon'} />;
      case 5:
        return <DarkPCP className={'navigation-icon'} />;
      case 6:
        return <DarkPlungerLift className={'navigation-icon'} />;
      case 7:
        return <DarkGasLift className={'navigation-icon'} />;
      case 8:
        return <OrifaceGasMeter className={'navigation-icon'} />;
      case 9:
        return <DarkLiquidMeter className={'navigation-icon'} />;
      case 10:
        return <DarkTank className={'navigation-icon'} />;
      case 11:
        return <DarkPID className={'navigation-icon'} />;
      case 12:
        return <DarkValve className={'navigation-icon'} />;
      case 13:
        return <DarkInjection className={'navigation-icon'} />;
      case 14:
        return <DarkPump className={'navigation-icon'} />;
      case 15:
        return <DarkJetLift className={'navigation-icon'} />;
      case 16:
        return <DarkPlungerLift className={'navigation-icon'} />;
      case 17:
        return <DarkChemicalInjection className={'navigation-icon'} />;
      case 18:
        return <DarkFacilityLift className={'navigation-icon'} />;
      default:
        return <DarkDefault className={'navigation-icon'} />;
    }
  };

  const throttledSearch = debounce(performSearch, 100);

  const VirtualizedSearchItem = memo(({ index, style }: { index: number; style: React.CSSProperties }) => {
    const item = searchResults[index];
    if (!item || !Object.keys(item).length) {
      return <div style={style}></div>;
    }
    return (
      <div style={style}>
        <div
          key={`${item.searchType}-${item.assetGUID || item.assetGroupId}-${index}`}
          className='search-item'
          onClick={() => openSearchItem(item)}
        >
          <div className='search-item-content'>
            <div className='search-item-inner'>
              {item?.id && (
                <div className='recent-icon-container'>
                  <RecentIcon />
                </div>
              )}
              <div className='search-item-details'>
                <div className={`search-item-layout ${item.searchType === 'Group' ? 'group-layout' : 'well-layout'}`}>
                  <span className={`search-item-name ${showWellStatus ? '' : '!ml-[25px]'}`}>
                    {item?.assetName || item?.groupName}
                  </span>
                  <div className={`search-item-icon ${showWellStatus ? '' : '!left-0'}`}>
                    {item.searchType === 'Group' ? <WellGroupIcon /> : showAssetIcon(item)}
                  </div>
                  {item.searchType !== 'Group' && showWellStatus && (
                    <div className='well-status-icon'>
                      {item?.enabled ? (
                        <WellEnabledIcon className='well-enabled-icon' />
                      ) : (
                        <WellDisabledIcon className='well-disabled-icon' />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const renderNoResults = () => (
    <div className='no-results-container'>
      <div className='no-results-content'>
        <div className='no-results-text'>No results found</div>
      </div>
    </div>
  );

  return (
    <span
      ref={rootRef}
      className={`global-well-history-search ${className}  ${isExpanded ? 'expanded' : ''}`}
      tabIndex={0}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setShowSearchResult(false);
          setIsExpanded(false);
          onCollapse?.();
        }
      }}
    >
      <SearchIcon
        className='search-icon'
        width='20'
        height='20'
        stroke={isRenderedfromLocationsModule ? '#FCFCFD' : '#647980'}
        onClick={() => {
          setIsExpanded((v) => {
            const next = !v;
            if (next) {
              setShowSearchResult(true);
              requestAnimationFrame(() => {
                const el = inputRef.current;
                if (!el) return;
                el.focus();
                const len = el.value?.length ?? 0;
                try {
                  el.setSelectionRange?.(len, len);
                } catch {
                  ('');
                }
              });
            } else {
              setShowSearchResult(false);
            }
            return next;
          });
          onIconClick?.();
        }}
      />
      <input
        ref={inputRef}
        className={`search-input ${className} ${inputStyle} text-[#f7f9f9] ${isRenderedfromLocationsModule ? 'search-input-custom-placeholder-design' : ''}`}
        placeholder={placeholderText}
        value={searchValue}
        onClick={showSearchHistory}
        onChange={handleSearchInputChange}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setIsExpanded(false);
            setShowSearchResult(false);
            onCollapse?.();
          }
          if (e.key === 'Enter') {
            setIsExpanded(false);
            setShowSearchResult(false);
            onCollapse?.();
          }
        }}
      />

      {searchValue && (
        <button className='clear-search-button' onClick={clearSearch} type='button' aria-label='Clear search'>
          ✕
        </button>
      )}
      {showSearchResult && (
        <div className={` search-dropdown ${className}`} tabIndex={-1} onMouseDown={(e) => e.preventDefault()}>
          {loading ? (
            <div className='search-loader'>
              <Loader />
            </div>
          ) : searchResults?.length === 0 ? (
            renderNoResults()
          ) : (
            <div className='search-results-container'>
              <List
                height={296}
                itemCount={searchResults.length}
                itemSize={60}
                width='100%'
                className='search-results-list-virtualized'
              >
                {VirtualizedSearchItem}
              </List>
            </div>
          )}
        </div>
      )}
    </span>
  );
};

export default GlobalWellHistorySearch;
